import { useEffect, useState } from 'react';
import { buildCanonicalEnergyActivitySeries } from '../domains/capAnchoredActivity/canonical/buildCanonicalEnergyActivitySeries';
import { buildLegacyContextFromFetches } from '../domains/capAnchoredActivity/canonical/legacyContext';
import { BURBANK_EIA861_PILOT_POLICY } from '../domains/capAnchoredActivity/canonical/types';
import { BURBANK_GEOID } from '../domains/eia861UtilityElectricity/bwpObservations';
import { getBuildingsEnergyData } from '../services/buildingsEnergyDataService';
import {
  fetchElectricitySeries,
  fetchNaturalGasSeries,
} from '../components/charts/EnergyUseSupabase';

export function useBurbankReconciledCanonical(location: string | null, enabled: boolean) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [electricity, setElectricity] = useState<
    ReturnType<typeof buildCanonicalEnergyActivitySeries> | null
  >(null);
  const [gas, setGas] = useState<ReturnType<typeof buildCanonicalEnergyActivitySeries> | null>(null);

  useEffect(() => {
    if (!enabled || !location) {
      setElectricity(null);
      setGas(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [elecRes, elecCom, gasRes, gasCom, buildings, previousCpBuildings] = await Promise.all([
          fetchElectricitySeries(location, 'residential', 'average', 'average'),
          fetchElectricitySeries(location, 'commercial', 'average', 'average'),
          fetchNaturalGasSeries(location, 'residential', 'average', 'average'),
          fetchNaturalGasSeries(location, 'commercial', 'average', 'average'),
          getBuildingsEnergyData(location),
          getBuildingsEnergyData(location, { applyEia861UtilityPilotOverride: false }),
        ]);
        if (cancelled) return;
        const legacy = buildLegacyContextFromFetches(
          elecRes,
          elecCom,
          gasRes,
          gasCom,
          buildings,
          previousCpBuildings
        );
        setElectricity(
          buildCanonicalEnergyActivitySeries(BURBANK_EIA861_PILOT_POLICY, BURBANK_GEOID, 'electricity', legacy)
        );
        setGas(
          buildCanonicalEnergyActivitySeries(BURBANK_EIA861_PILOT_POLICY, BURBANK_GEOID, 'natural_gas', legacy)
        );
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load reconciled energy series');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [location, enabled]);

  return { loading, error, electricity, gas };
}
